import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Brand } from "./components/Brand";
import { useDealerSession } from "./lib/session";
import { ConfirmDialog } from "./components/ConfirmDialog";
import { dealerApi } from "./lib/api";
import { tzs } from "./lib/format";
import {
  ChevronLeft,
  Coins,
  FileText,
  Landmark,
  LayoutDashboard,
  Menu,
  Percent,
  SendHorizontal,
  Settings,
  ShoppingBag,
  Users,
} from "lucide-react";

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
}

const navGroups: { label: string; items: NavItem[] }[] = [
  {
    label: "Overview",
    items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard }],
  },
  {
    label: "Network",
    items: [{ to: "/agents", label: "My Agents", icon: Users }],
  },
  {
    label: "Money",
    items: [
      { to: "/topup", label: "Bank Top-up", icon: Landmark },
      { to: "/send", label: "Send Float", icon: SendHorizontal },
      { to: "/transactions", label: "Transactions", icon: ShoppingBag },
      { to: "/commissions", label: "Commissions", icon: Percent },
      { to: "/statement", label: "Statement", icon: FileText },
    ],
  },
  {
    label: "Account",
    items: [{ to: "/settings", label: "Settings", icon: Settings }],
  },
];

const pageTitles: Record<string, string> = Object.fromEntries(
  navGroups.flatMap((g) => g.items.map((i) => [i.to, i.label])),
);

export function Dealer() {
  const { dealer, loading, signOut } = useDealerSession();
  const location = useLocation();
  const navigate = useNavigate();
  // The sidebar is an off-canvas drawer below 768px, so it starts closed there.
  const [sidebarOpen, setSidebarOpen] = useState(
    () => !window.matchMedia("(max-width: 768px)").matches,
  );
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // Live float for the topbar chip; shares the ["dealer-wallet"] cache with
  // the Send-float screen so one poll keeps both honest.
  const wallet = useQuery({
    queryKey: ["dealer-wallet"],
    queryFn: () =>
      dealerApi.get<{ available_minor: number; reserved_minor: number; commission_minor: number }>(
        "/superdealer/v1/wallet",
      ),
    refetchInterval: 15_000,
    enabled: Boolean(dealer),
  });

  const toggleSidebar = useCallback(() => setSidebarOpen((p) => !p), []);

  // Close the drawer when the viewport becomes mobile-sized.
  useEffect(() => {
    const query = window.matchMedia("(max-width: 768px)");
    const handleChange = (event: MediaQueryListEvent) => {
      if (event.matches) setSidebarOpen(false);
    };
    query.addEventListener("change", handleChange);
    return () => query.removeEventListener("change", handleChange);
  }, []);

  // Navigating on a phone should dismiss the drawer.
  useEffect(() => {
    if (window.matchMedia("(max-width: 768px)").matches) setSidebarOpen(false);
  }, [location.pathname]);

  const handleSignOut = useCallback(() => {
    signOut();
    navigate("/login");
  }, [signOut, navigate]);

  useEffect(() => {
    if (!loading && !dealer && location.pathname !== "/login") {
      navigate("/login", { replace: true });
    }
  }, [dealer, loading, location.pathname, navigate]);

  if (!dealer && location.pathname !== "/login") {
    return null;
  }

  const activeRoute = pageTitles[location.pathname];
  const pageHeading = activeRoute ?? "evocha";

  if (location.pathname === "/login") return <Outlet />;

  return (
    <div className="admin-shell">
      {/* ── Sidebar ── */}
      <aside
        className={`admin-sidebar ${sidebarOpen ? "admin-sidebar-open" : "admin-sidebar-collapsed"}`}
        aria-label="Main navigation"
      >
        <div className="admin-sidebar-head">
          <Brand compact />
          <button
            className="ev-icon-btn"
            aria-label={sidebarOpen ? "Close menu" : "Open menu"}
            onClick={toggleSidebar}
          >
            <ChevronLeft size={18} />
          </button>
        </div>

        <nav className="admin-nav" role="navigation">
          {navGroups.map((group) => (
            <div key={group.label} className="dealer-nav-group">
              <span className="admin-nav-label">{group.label}</span>
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.to;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={
                      isActive ? "admin-nav-item admin-nav-item-active" : "admin-nav-item"
                    }
                  >
                    <Icon size={18} strokeWidth={1.6} />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="admin-sidebar-foot">
          <div className="admin-sidebar-user">
            <span className="ev-avatar ev-avatar-sm">
              {(dealer?.business_name ?? "D").charAt(0).toUpperCase()}
            </span>
            <div className="admin-user-text">
              <strong>{dealer?.business_name ?? "Dealer"}</strong>
              <small className="ev-body-micro">{dealer?.code ?? ""}</small>
            </div>
          </div>
          <button
            className="ev-btn ev-btn-sm ev-btn-outline"
            onClick={() => setShowLogoutConfirm(true)}
          >
            Sign out
          </button>
        </div>
      </aside>

      {sidebarOpen && (
        <button
          className="admin-scrim"
          aria-label="Close navigation"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Main ── */}
      <div className="admin-main">
        <header className="admin-topbar">
          <div className="admin-topbar-left">
            <button
              className="ev-icon-btn admin-menu-toggle"
              aria-label={sidebarOpen ? "Close menu" : "Open menu"}
              onClick={toggleSidebar}
            >
              {sidebarOpen ? <ChevronLeft size={18} /> : <Menu size={18} />}
            </button>
            <div>
              <div className="ev-eyebrow">Dealer portal</div>
              <h1 className="admin-topbar-title">{pageHeading}</h1>
            </div>
          </div>
          <div className="admin-topbar-right">
            <button
              className="live-balance"
              onClick={() => navigate("/")}
              title="Live dealership float — refreshes automatically"
            >
              <Coins size={15} />
              <span className="live-balance-value">
                {wallet.isPending ? "—" : tzs(wallet.data?.available_minor ?? 0)}
              </span>
              <span
                className={`live-balance-dot${wallet.isFetching ? " live-balance-pulse" : ""}`}
                aria-hidden
              />
            </button>
            <span className="dash-date-pill">{dealer?.code}</span>
          </div>
        </header>

        <div className="admin-content">
          <div className="page" key={location.pathname}>
            <Outlet />
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleSignOut}
        title="Sign out?"
        message="Are you sure you want to sign out of the dealer portal?"
        confirmLabel="Sign out"
        cancelLabel="Cancel"
      />
    </div>
  );
}
